import { Radio, Stack, Tab, Tabs } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyledSelectField } from '../../Common/Components/Forms/StyledSelectField';
import { ContentPanel, StepDefinition, usePanel } from 'src/Common/Components/ContentControl/ContentPanel';
import { enqueueSnackbar } from 'notistack';
import { AllInbox, Link } from '@mui/icons-material';
import { createBlobDownload } from 'src/Common/Helper/BlobDownload';
import useRequest from 'src/Common/Hooks/useRequest';
import useEkko from 'src/Common/Providers/EkkoProvider';
import useDataTable from 'src/Common/Providers/DataTableProvider';

interface Props {
  closeHandler: () => void;
}

const FormatTypeMap: Record<string, Record<string, string>> = {
  csv: {
    text: 'CSV',
    contentType: 'text/csv',
  },
  json: {
    text: 'JSON',
    contentType: 'application/json',
  },
};

const FileExtMap: Record<string, string> = {
  'application/json': 'json',
  'text/plain': 'csv',
  'text/csv': 'csv',
};

export const ExportPanel = ({ closeHandler }: Props): React.JSX.Element => {
  const { api, config } = useEkko();
  const { filtering } = useDataTable();

  const { enableSave } = usePanel();
  const [isExporting, startExporting] = useRequest();

  const [entity, setEntity] = useState<'compartment' | 'link'>('compartment');
  const [selectedFormat, setSelectedFormat] = useState<string>();
  const [formats, setFormats] = useState<string[]>([]);

  const availableFormats = useMemo(() => config.exportFormatTypes, [config.exportFormatTypes]);

  useEffect(() => {
    enableSave(!isExporting);
  }, [isExporting]);

  useEffect(() => {
    const validTypes = Object.keys(FormatTypeMap);
    const prunedTypes = availableFormats.filter((t) => {
      if (!validTypes.includes(t)) {
        enqueueSnackbar(`Unknown export type "${t}" found in config`);
        return false;
      }
      return true;
    });

    if (prunedTypes.length === 0) {
      enqueueSnackbar('Export format config has no valid types');
      closeHandler();
    }

    setFormats(prunedTypes);
    setSelectedFormat(prunedTypes[0]);
  }, [availableFormats]);

  const exportEntities = useCallback(() => {
    if (selectedFormat === undefined) {
      return;
    }

    const { contentType } = FormatTypeMap[selectedFormat];

    startExporting(
      async () => {
        let blob: Promise<Blob>;
        if (entity === 'compartment') {
          blob = api.exportCompartments(filtering, contentType);
        } else {
          blob = api.exportLinks(filtering, contentType);
        }

        return await blob;
      },
      (blob) => {
        const timestamp = Date.now();
        const filename = `${entity}s_${timestamp}.${FileExtMap[contentType]}`;
        createBlobDownload(blob as Blob, filename);
      },
      (e) => {
        enqueueSnackbar(e.message, { variant: 'error' });
      },
    );
  }, [filtering, entity, selectedFormat]);

  const formatOptions = useMemo(
    () =>
      formats.map((format, i) => (
        <option key={i} value={format}>
          {format}
        </option>
      )),
    [formats],
  );

  const step: StepDefinition = useMemo(
    () => ({
      title: 'Export',
      contentCallback: () => (
        <Stack direction={'column'} alignItems={'center'} justifyContent={'flex-start'} spacing={4}>
          <Tabs value={entity} onChange={(_, value) => setEntity(value)} indicatorColor="primary" textColor="secondary">
            {['compartment', 'link'].map((ent: string) => {
              const description = ent[0].toUpperCase() + ent.slice(1) + 's';
              const icon = ent === 'link' ? <Link style={{ margin: 11 }} /> : <AllInbox style={{ margin: 11 }} />;

              return (
                <Tab
                  key={ent}
                  value={ent}
                  style={{
                    display: 'block',
                    maxWidth: 400,
                  }}
                  label={
                    <div
                      style={{
                        display: 'inline-flex',
                        justifyContent: 'center',
                        lineHeight: '48px',
                      }}
                    >
                      <Radio checked={entity === ent} />
                      {icon}
                      {description}
                    </div>
                  }
                />
              );
            })}
          </Tabs>
          <div style={{ width: '25%' }}>
            <StyledSelectField
              native
              value={selectedFormat}
              onChange={(e) => setSelectedFormat(String(e.target.value))}
              label="Type"
            >
              {formatOptions}
            </StyledSelectField>
          </div>
        </Stack>
      ),
    }),
    [formatOptions, entity, selectedFormat],
  );

  return (
    <ContentPanel
      steps={[step]}
      onClose={closeHandler}
      onSave={exportEntities}
      onCancel={closeHandler}
      buttonLabels={{
        save: 'Export',
      }}
    />
  );
};
